import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Award, Plus, Search, Pencil, Trash2 } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { PlacementModal } from '../components/crm/PlacementModal';
import {
  INVOICE_STATUSES, invoiceStatusMeta, placementStatusMeta, fmtMoney, fmtDate,
} from '../components/crm/constants';

const FILTERS = [
  { key: 'all', label: 'All' },
  ...INVOICE_STATUSES.map((s) => ({ key: s.value, label: s.label })),
  { key: 'fell_out', label: 'Fell Out' },
];

const KPI = ({ label, value, tone, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 text-center">
      <p className={`text-xl font-bold ${tone || 'text-gray-900'}`}>{value}</p>
      <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
    </CardContent>
  </Card>
);

export default function SalesPlacements() {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'jobasti_admin';
  const [placements, setPlacements] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState({ open: false, placement: null });

  const load = useCallback(async () => {
    const [pRes, cRes] = await Promise.all([crmFetch('/placements'), crmFetch('/commission')]);
    if (pRes.tableMissing) setTableMissing(true);
    else if (pRes.success) { setPlacements(pRes.data || []); setTableMissing(false); }
    if (cRes.success && cRes.data) setSnapshot(cRes.data.snapshot);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setInvoice = async (p, invoiceStatus) => {
    if (invoiceStatus === p.invoice_status) return;
    const res = await crmFetch(`/placements/${p.id}`, { method: 'PATCH', body: JSON.stringify({ invoiceStatus }) });
    if (res.success) { toast.success(`Marked ${invoiceStatusMeta(invoiceStatus).label}`); load(); }
    else toast.error(res.message || 'Failed to update invoice status');
  };

  const deletePlacement = async (p) => {
    if (!window.confirm(`Delete the placement for ${p.candidate_name}?`)) return;
    const res = await crmFetch(`/placements/${p.id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Placement deleted'); load(); }
  };

  const q = search.trim().toLowerCase();
  const filtered = placements.filter((p) => {
    if (filter === 'fell_out' && p.status !== 'fell_out') return false;
    if (filter !== 'all' && filter !== 'fell_out' && (p.invoice_status !== filter || p.status === 'fell_out')) return false;
    if (!q) return true;
    return [p.candidate_name, p.job_title, p.accountName].some((v) => (v || '').toLowerCase().includes(q));
  });

  const colCount = isAdmin ? 9 : 8;

  return (
    <SalesLayout>
      <div data-testid="placements-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Placements</h1>
            <p className="text-sm text-gray-500 mt-1">Successful placements, fee revenue and invoicing.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModal({ open: true, placement: null })} data-testid="record-placement-btn">
            <Plus className="w-4 h-4 mr-2" /> Record Placement
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              Placement tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-06-16-crm-phase3.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI label="Placements YTD" value={loading ? '…' : snapshot?.placementsYtd ?? 0} testId="placements-kpi-count" />
              <KPI label="Revenue YTD" value={loading ? '…' : (fmtMoney(snapshot?.revenueYtd ?? 0) || '€0')} testId="placements-kpi-revenue" />
              <KPI label="Commission Earned" value={loading ? '…' : (fmtMoney(snapshot?.earnedYtd ?? 0) || '€0')} tone="text-emerald-600" testId="placements-kpi-earned" />
              <KPI label="Commission Pending" value={loading ? '…' : (fmtMoney(snapshot?.pending ?? 0) || '€0')} tone="text-amber-600" testId="placements-kpi-pending" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-9" placeholder="Search placements…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="placements-search-input" />
              </div>
              <div className="flex gap-1 flex-wrap">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filter === f.key ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`placements-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 font-medium">Candidate</th>
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium">Start</th>
                        <th className="px-4 py-3 font-medium">Salary</th>
                        <th className="px-4 py-3 font-medium">Revenue</th>
                        <th className="px-4 py-3 font-medium">Commission</th>
                        <th className="px-4 py-3 font-medium">Invoice</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        {isAdmin && <th className="px-4 py-3 font-medium">Owner</th>}
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={colCount + 1} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                      {!loading && filtered.length === 0 && (
                        <tr><td colSpan={colCount + 1} className="px-4 py-10 text-center text-gray-400">
                          {placements.length === 0 ? 'No placements yet — record your first win 🎉' : 'No placements match your filters.'}
                        </td></tr>
                      )}
                      {filtered.map((p) => {
                        const st = placementStatusMeta(p.status);
                        const fellOut = p.status === 'fell_out';
                        return (
                          <tr key={p.id} className={`border-b last:border-0 transition-colors ${fellOut ? 'opacity-60' : 'hover:bg-gray-50/60'}`} data-testid={`placement-row-${p.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                                  <Award className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{p.candidate_name}</p>
                                  <p className="text-xs text-gray-500">{p.job_title}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{p.accountName || '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{p.start_date ? fmtDate(p.start_date) : '—'}</td>
                            <td className="px-4 py-3 text-gray-600">{fmtMoney(p.salary, p.currency) || '—'}</td>
                            <td className={`px-4 py-3 font-medium ${fellOut ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{fmtMoney(p.revenue, p.currency) || '—'}</td>
                            <td className={`px-4 py-3 font-medium ${fellOut ? 'text-gray-400 line-through' : 'text-emerald-600'}`}>{fmtMoney(p.commission_amount, p.currency) || '—'}</td>
                            <td className="px-4 py-3">
                              <Select value={p.invoice_status} onValueChange={(v) => setInvoice(p, v)}>
                                <SelectTrigger className="h-8 w-28 text-xs" data-testid={`placement-invoice-select-${p.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {INVOICE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-4 py-3"><Badge className={`${st.className} border-0`}>{st.label}</Badge></td>
                            {isAdmin && <td className="px-4 py-3 text-gray-600">{p.ownerName || '—'}</td>}
                            <td className="px-4 py-3">
                              <div className="flex gap-0.5 justify-end">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setModal({ open: true, placement: p })} data-testid={`edit-placement-${p.id}`}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => deletePlacement(p)} data-testid={`delete-placement-${p.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <PlacementModal
        open={modal.open}
        onOpenChange={(o) => setModal((s) => ({ ...s, open: o }))}
        onSaved={load}
        placement={modal.placement}
      />
    </SalesLayout>
  );
}
